import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-form';
import { find, findIndex } from 'lodash';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as queryString from 'query-string';

import EditDivisionRegistration from './EditDivisionRegistration';
import { Input } from '../Form';
import InfoButton from '../InfoButton';
import { Utr, UtrLabel } from '../text';
import Description from '../text/Description';
import TrackedClick, { trackEvent } from '../Analytics/TrackedClick';
import {
    platform,
    PowerFunnelGatedFeatures,
    PowerFunnelSurfaces,
    UpgradeFunnelEvents,
} from '../Analytics/constants';
import { deriveUserSubscriptionStatus } from '../Analytics/userSubscriptionStatus';
import { useUpgradeFlowAbandonmentTracking } from '../hooks/useUpgradeFlowAbandonmentTracking';
import Img from '../Img';
import EventQuestion from './EventQuestion';
import PaymentStep from './PaymentStep';
import PaymentWaitStep from './PaymentWaitStep';

import { getClickAttrs } from '../Analytics/gtag';
import { gameTypes, nonPoweredPlayerDefaultDivisionFee, ratingTypes } from '../../utils/constants';
import { displayFee, getRegistrationTotal, isEventType, EventTypes, isEventPTT, areAllPlayableDivisionsAnnualPowerOnly, isAnyPlayableDivisionAnnualPowerOnly } from '../../utils/events';
import {
    clubJoinRequestTypeIds,
    sportTypeIds,
    regStepIds,
    utrTypeIds,
    eventQuestionTypeIds,
    freeDivision,
    needsPartnerText,
    divisionRegistrationTypeIds,
    eventFlows,
} from '../../utils/eventConstants';
import AnnualPowerUpsell from '../AnnualPowerUpsell';
import SidebarPowerCheckout from './SidebarPowerCheckout';

import DivisionSelectionV2 from './DivisionSelectionV2';
import PartnerSelectionV2 from './PartnerSelectionV2';
import CampRegistrationSelection from './CampRegistrationSelection';
import CampDivisionSelection from './CampDivisionSelection';

import { useStatsig } from '../hooks/useStatsig';
import { required } from '../../utils/form-validation';
import { createValidateUsapIdCallback } from '../../utils/usap';

import config from '../../../config';
import { eventShape, userShape, wizardStepShape, routerLocationShape, playerShape } from '../../propTypes';
import styles from './eventRegForm.scss';
import CampConfirmation from './CampConfirmation';
import { getNewClubPath } from '../../utils/clubs';

const _ = { find, findIndex };

// supports V2 typeIds
function EventRegForm(props) {
    const {
        selectedDivisionIds,
        event,
        user,
        sidebarFlow,
        stripePopulateSwitchPaymentRequestBody,
        stripePopulatePaymentRequestBody,
        handlePromoCodeApplication,
        promoCodeSuccess,
        handlePromoCodeRemoval,
        promoCodeFailure,
        promoCode,
        handlePromoCodeChange,
        invalidPromoCodePlaceHolder,
        promoCodeApplicationText,
        totalDiscount,
        serviceFee = 0,
        registrationTypeForCamps,
        setRegistrationTypeForCamps,
        setSidebarFlow,
        multipleDivisionRegistrations,
        handleMultipleDivisionRegistrations,
        setSelectedDivisionIds,
        getMultiRegistrationTotal,
        handleClearMultipleDivisionRegistrations,
        location: pageLocationObject,
    } = props;

    const parsed = queryString.parse(pageLocationObject?.search);
    const { jwt } = parsed;

    const { registrations } = useSelector(({ eventProfile }) => {
        const { registrations } = eventProfile;
        return { registrations };
    });

    const [teammate, setTeammate] = useState(props.step.invite);
    const [checkoutId, setCheckoutId] = useState(null);
    const UPGRADE_SUB_STEPS = { UPSELL: 'upsell', CHECKOUT: 'checkout' };
    const [upgradeSubStep, setUpgradeSubStep] = useState(UPGRADE_SUB_STEPS.UPSELL);

    useEffect(() => {
        if (props.step.invite !== teammate) {
            setTeammate(props.step.invite);
        }
    }, [props.step.invite]);

    const userSubscriptionStatus = deriveUserSubscriptionStatus({ user, isMonthlySubscriber: props.isMonthlySubscriber });
    const sourceEventId = props.event?.id;

    const isInAnnualPowerUpgradeStep = props.step.id === regStepIds.UPGRADE &&
        areAllPlayableDivisionsAnnualPowerOnly(props.event);

    const { markSucceeded: handleUpgradeSucceeded } = useUpgradeFlowAbandonmentTracking({
        isInUpgradeStep: isInAnnualPowerUpgradeStep,
        userSubscriptionStatus,
        sourceEventId,
        referringSurface: PowerFunnelSurfaces.EVENT_REGISTRATION,
        gatedFeature: PowerFunnelGatedFeatures.ANNUAL_POWER_DIVISION,
    });

    const handleGetAnnualPlan = () => {
        trackEvent(UpgradeFunnelEvents.CTA_CLICKED, {
            platform,
            userSubscriptionStatus,
            referringSurface: PowerFunnelSurfaces.EVENT_REGISTRATION,
            gatedFeature: PowerFunnelGatedFeatures.ANNUAL_POWER_DIVISION,
            ...(sourceEventId && { sourceEventId }),
        });
        setUpgradeSubStep(UPGRADE_SUB_STEPS.CHECKOUT);
    };

    const eventVerificationFeeParsed = parseFloat(event.verificationFee) || 0;
    const clubVerificationFee = eventVerificationFeeParsed >= 0 ? eventVerificationFeeParsed : nonPoweredPlayerDefaultDivisionFee;
    const clubVerificationFeeString = clubVerificationFee?.toFixed(2);

    const claimedPlayer = user?.claimedPlayer || {};
    const paymentProviderType = event?.paymentProviderType?.id || event.paymentProviderTypeId;
    const { sportTypeId } = event || {};
    const isFlexLeagueEvent = isEventType(event, EventTypes.FLEX_LEAGUE);
    const onlyHomeCourtQuestion = event?.eventQuestions?.length === 1 && event?.eventQuestions[0].typeId === eventQuestionTypeIds.HOME_COURT;

    const { checkGate, featureGates } = useStatsig();
    const isPromoCodesEnabled = checkGate(featureGates.PROMO_CODES);
    const isClubRedirectEnabled = checkGate(featureGates.CLUB_LINKS_REDIRECT);
    const isNeedPartnerSelectionEnabled = checkGate(featureGates.ALLOW_NEED_PARTNER_PLAYER_SELECTION);
    const clubTierTypeId = event?.club?.tierTypeId;
    const newClubPath = getNewClubPath(clubTierTypeId);
    // we wanted to preserve the pure division cost on the step before but only tack on service fees on this screen and the stripe form
    // calculate registration total and only add service fees if there are divisions that need to be paid for
    const { updatedTotal: registrationTotal, updatedServiceFee } = getRegistrationTotal({ total: props.total, serviceFee });
    // only display service fee if there is any amount that needs to be paid and if the api returns a service fee based on the feature flag
    const displayServiceFee = registrationTotal > 0;

    const usapQuestionTypeId = 7;

    const validatePassword = async (value) => {
        const isEmpty = !value || value.trim() === '';
        if (isEmpty) {
            return { error: 'Please enter a password.' };
        }

        const valid = await props.handleValidatePassword({ answer: value });
        return { error: valid ? null : 'Invalid password' };
    };

    const validateUSAPId = createValidateUsapIdCallback(jwt, event.id);

    const selectedPlayer = (step) => {
        const {
            // eslint-disable-next-line no-shadow
            selectedPlayer,
            utrType,
        } = step;

        const {
            firstName,
            lastName,
            gender,
            location,
            id,
            nationality,
            profileImage,
        } = selectedPlayer;

        const nationalFlag = nationality ? `/img/flags/${nationality.toLowerCase()}.svg` : '';

        const descriptionItems = [
            ...(gender ? [gender] : []),
            ...(location?.display ? [location.display] : []),
        ];

        const isTennis = sportTypeId === sportTypeIds.TENNIS;
        const isPickleball = sportTypeId === sportTypeIds.PICKLEBALL;

        return (
            <div className="utr-card player-card">
                <div className="card-view">
                    <div className="row">
                        <div className="col-8">
                            <div className="name-and-place">
                                <div className="name h6" data-testid="event-reg.added-player.name">
                                    <Link
                                        to={`/profiles/${id}`}
                                        target="_blank"
                                    >
                                        <span className="text-medium">{firstName}</span> {lastName}
                                    </Link>
                                </div>
                                <div className="place">
                                    <Description desc={descriptionItems} />
                                </div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="avatar-container ml-auto">
                                {!profileImage ? (
                                    <Img
                                        src={'/img/v2/icons/icon-avatar-player.svg'}
                                        className="avatar"
                                    />
                                ) : (
                                    <Img
                                        src={`/v1/player/${profileImage}?h=120&w=120`}
                                        className="avatar"
                                        onError={(e) => {
                                            e.target.src = '/img/v2/icons/icon-avatar-player.svg';
                                        }}
                                    />
                                )}
                                {nationality && (
                                    <div className="flag-container">
                                        <Img
                                            className="flag"
                                            src={nationalFlag}
                                            alt={nationality}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {(selectedPlayer.profileId > 0 || selectedPlayer.playerId > 0) && (
                        <div className="row profile-details">
                            <div className="col-4">
                                <div className="values">
                                    <div className="title">
                                        {isTennis ? <UtrLabel player={selectedPlayer} force={{ ratingType: gameTypes.SINGLES, utrType, typeDots: true }} sportTypeId={sportTypeId} /> : `${ratingTypes.pickleball} (S)`}
                                    </div>
                                    <div
                                        className={`value`}
                                    >
                                        <Utr player={selectedPlayer} force={{ ratingType: gameTypes.SINGLES, utrType, verifiedIcon: true }} sportTypeId={sportTypeId} />
                                    </div>
                                </div>
                            </div>
                            {(isTennis || isPickleball) && (
                                <div className="col-4">
                                    <div className="values">
                                        <div className="title">
                                            {isTennis ? <UtrLabel player={selectedPlayer} force={{ ratingType: gameTypes.DOUBLES, utrType, typeDots: true }} sportTypeId={sportTypeId} /> : `${ratingTypes.pickleball} (D)`}
                                        </div>
                                        <div
                                            className={`value`}
                                        >
                                            <Utr player={selectedPlayer} force={{ ratingType: gameTypes.DOUBLES, utrType, verifiedIcon: true }} sportTypeId={sportTypeId} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={`col-4 justify-content-end`} />
                        </div>
                    )}
                    {!(selectedPlayer.profileId || selectedPlayer.playerId) && (
                        <div className="row profile-details">
                            <div className="col">
                                <div className={`form-group ${(step.errors.existingEmail) && 'is-invalid'}`}>
                                    <label htmlFor="email" data-testid="event-reg.partner-email.label">Please enter your partners’s email</label>
                                    <div className={`${styles.partnerEmailDescription} text-small mb16`} data-testid="event-reg.partner-email.description">Your partner will receive an invitation email to register. They must create an account on UTR Sports using the provided link in order to complete their registration. Once they do, they will appear on the registered players list next to your name.</div>
                                    <div className="input-wrapper">
                                        <input
                                            type="text"
                                            id="email"
                                            placeholder="name@example.com"
                                            className="form-control"
                                            value={step.selectedPlayer.email}
                                            onChange={props.addUnclaimedEmail}
                                            data-testid="event-reg.partner-email.input"
                                        />
                                        <div className="error-indicator" />
                                    </div>
                                    {step.errors.existingEmail && <div className="invalid-feedback">{step.errors.existingEmail}</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    let showClubJoin = false;
    const currentClubMembership = user && _.find(user.clubMemberships, { clubId: event.club.id });
    const currentClubMemberRoles = ['Owner', 'Admin', 'Member'];
    if (event?.eventClubJoinRequestType?.value === 'join' || event?.eventClubJoinRequestTypeId === clubJoinRequestTypeIds.JOIN) {
        if (!(currentClubMembership?.roleValue && currentClubMemberRoles.indexOf(currentClubMembership.roleValue) !== -1)) {
            showClubJoin = true;
        }
    }

    const showOptInForEventDeskNotificationsCheckBox = event?.enableEventDeskNotifications;

    const areAllDivisionsAnnualPowerOnly = areAllPlayableDivisionsAnnualPowerOnly(event);
    const areAnyDivisionsAnnualPowerOnly = isAnyPlayableDivisionAnnualPowerOnly(event);

    const isRegistered = event?.registered || event?.currentMember.isPlaying;

    const checkDivisionIsSelected = (division) => selectedDivisionIds.includes(division.id);

    const playersToShowEventQuestions = props.unRegisteredPlayers?.filter((player) => {
        return player.canAnswerEventQuestions;
    }) || [];

    const sortEventQuestions = () => {
        const isUsapQuestion = event?.eventQuestions.find((q) => q.typeId === usapQuestionTypeId);
        if (isUsapQuestion) {
            return [isUsapQuestion, ...event.eventQuestions.filter(q => q.typeId !== usapQuestionTypeId)];
        }
        return event?.eventQuestions;
    };
    const eventQuestions = sortEventQuestions();

    const adminAddedPaymentRequiredDivisionIds = [...new Set(event?.currentMember?.registeredDivisions?.filter(division => division.divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED).map(division => division.id))];

    const isAdminAddedPaymentRequired = (division) => {
        return adminAddedPaymentRequiredDivisionIds.includes(division.id);
    };

    const getCheckoutRequest = () => {
        // depending on which flow you're using,
        // we must construct a different checkout request
        if (props.step.id === regStepIds.PAYMENT) {
            if (sidebarFlow === regStepIds.DIVISIONS_SWITCH) {
                // populate post data for switch divisions api call
                return stripePopulateSwitchPaymentRequestBody();
            }
            // populate post data for normal registration
            return stripePopulatePaymentRequestBody();
        }
        return null;
    };
    const checkoutRequest = getCheckoutRequest();

    const allSelectedDivisionsAreFree = () => {
        let divisionsAreFree = true;
        event.eventDivisions.forEach((division) => {
            if (selectedDivisionIds.includes(division.id) && division.entryFee) {
                divisionsAreFree = false;
            }
        });

        if (sidebarFlow === eventFlows.CAMP_FLOW) {
            const hasPaidDivisions = Object.values(multipleDivisionRegistrations).some(({ quantity, price }) => quantity > 0 && price > 0);
            if (hasPaidDivisions) {
                divisionsAreFree = false;
            }
        }

        return divisionsAreFree;
    };

    const hasOnlyVerifiedFees = () => {
        if (sidebarFlow === eventFlows.CAMP_FLOW) {
            // add logic here to calculate if it's a free event but only verified fees
            // for MVP this would never be the case so just return false;
            return false;
        }
        const selectedDivisionsCount = selectedDivisionIds?.length || 0;
        const fee = parseFloat(event.verificationFee) || 0;
        // todo: why is this parsing and why is total passed in here at all? what about "eventFee" which was the calculated registration total?
        const regTotal = parseFloat(props.total) || 0;
        const verifiedTotal = fee * selectedDivisionsCount;
        const updatedTotal = regTotal - verifiedTotal;
        return updatedTotal === 0;
    };

    const isAlreadyRegisteredForEntryFeeDivision =
        Object.values(event.currentMember?.registeredDivisions ?? {})
            .some(div => div?.isEventEntryFeeDivision &&
                div?.paymentStatusTypeId === 4 &&
                div?.registrationStatusTypeId === 3) ?? false;

    const getStripeAccount = () => {
        // in a special case use UTR Sports stripe account
        const isSelectedDivisionsFree = allSelectedDivisionsAreFree() || hasOnlyVerifiedFees();
        const isSanctionedEventType = event.utrType?.id === utrTypeIds.SANCTIONED || event.utrTypeId === utrTypeIds.SANCTIONED;
        const shouldUseUtrStripeAccount = (!event.entryFee || isAlreadyRegisteredForEntryFeeDivision) && isSelectedDivisionsFree && (!user?.isPowered) && isSanctionedEventType;

        // todo: yeaaaah... this is a security flaw
        if (shouldUseUtrStripeAccount) {
            return config.utrStripeAccountId;
        }

        if (event.club?.stripeAccountId) {
            // use club's own stripe account
            return event.club?.stripeAccountId;
        }

        return null;
    };
    const stripeAccount = getStripeAccount();

    // intercept for state tracking, but deliver to parent so it can call toStep
    const onCheckoutProcessStarted = (newCheckoutId) => {
        setCheckoutId(newCheckoutId);
        props.onCheckoutProcessStarted();
    };

    const feeDescription = event?.sportTypeId === sportTypeIds.PICKLEBALL ? `Transaction Fees` : `Verified Fees`;

    const eventQuestionsDefaultValues = useMemo(() => {
        const homeCourtTypeQuestion = eventQuestions.find(question => question.typeId === eventQuestionTypeIds.HOME_COURT);
        if (homeCourtTypeQuestion) {
            const defValue = claimedPlayer?.homeCourt || '';
            return {
                [`withid-${claimedPlayer.playerId.toString()}-${homeCourtTypeQuestion.id}`]: defValue,
            };
        }
        return {};
    }, [event?.eventQuestions, claimedPlayer?.playerId, claimedPlayer?.homeCourt]);

    return (
        <div className="container">
            {props.step.id === regStepIds.UPGRADE && (
                <div className="row">
                    <div className="col">
                        {areAllDivisionsAnnualPowerOnly && upgradeSubStep === UPGRADE_SUB_STEPS.CHECKOUT && (
                            <SidebarPowerCheckout
                                onUpgradeComplete={props.onUpgradeComplete}
                                onUpgradeSucceeded={handleUpgradeSucceeded}
                                referringSurface={PowerFunnelSurfaces.EVENT_REGISTRATION}
                                gatedFeature={PowerFunnelGatedFeatures.ANNUAL_POWER_DIVISION}
                                sourceEventId={sourceEventId}
                                userSubscriptionStatus={userSubscriptionStatus}
                                setCheckoutSubmittingFlag={props.setCheckoutSubmittingFlag}
                            />
                        )}
                        {areAllDivisionsAnnualPowerOnly && upgradeSubStep !== UPGRADE_SUB_STEPS.CHECKOUT && (
                            <AnnualPowerUpsell
                                isPTTEvent={isEventPTT(event)}
                                isMonthlySubscriber={props.isMonthlySubscriber}
                                isAppleMonthlySubscriber={props.isAppleMonthlySubscriber}
                                onGetAnnualPlan={handleGetAnnualPlan}
                                ctaTextOverride={props.upsellCtaTextOverride}
                                redirectNoteOverride={props.upsellRedirectNoteOverride}
                                sourceEventId={sourceEventId}
                                userSubscriptionStatus={userSubscriptionStatus}
                                referringSurface={PowerFunnelSurfaces.EVENT_REGISTRATION}
                                gatedFeature={PowerFunnelGatedFeatures.ANNUAL_POWER_DIVISION}
                            />
                        )}
                        {!areAllDivisionsAnnualPowerOnly && (
                            <>
                                <div className={`${styles.upgradeToPowerContainer} p24`}>
                                    {areAnyDivisionsAnnualPowerOnly
                                        ? <p data-testid="event-reg.upgrade.annual-power-divisions-msg">Get <span className={`${styles.priceDiscount}`}>{props.event.currency.symbol || '$'}{clubVerificationFeeString}</span> off per registration division and unlock Annual Power Only divisions when you upgrade to a Power subscription.</p>
                                        : <p data-testid="event-reg.upgrade.standard-upgrade-msg">Get <span className={`${styles.priceDiscount}`}>{props.event.currency.symbol || '$'}{clubVerificationFeeString}</span> off per registration division when you upgrade to a Power subscription.</p>}
                                    {user.hasPastDueSubscription ? (
                                        <Link
                                            className={`btn btn-primary ${styles.upgradeToPowerButton}`}
                                            to="/subscriptions"
                                            {...getClickAttrs({
                                                category: 'Resume Power',
                                                context: `Resume Power Button Click - Event Registration`,
                                                location: 'Event Registration Form',
                                            })}
                                        >
                                            RESUME POWER
                                        </Link>
                                    ) : (
                                        <TrackedClick
                                            eventName="Power Upgrade Clicked"
                                            label="START POWER"
                                            traits={{
                                                pageLocation: 'Event Registration Power Popup',
                                                uxType: 'button',
                                                content: 'START POWER',
                                                subscriptionType: 'power',
                                            }}
                                        >
                                            <button
                                                className={`btn btn-primary ${styles.upgradeToPowerButton}`}
                                                type="button"
                                                onClick={props.handleUpgradeToPowerClick}
                                                {...getClickAttrs({
                                                    category: 'Power Upgrade',
                                                    context: `Start Power Button Click - Event Registration`,
                                                    location: 'Event Registration Form',
                                                })}
                                            >
                                                START POWER
                                            </button>
                                        </TrackedClick>
                                    )}
                                </div>
                                {!areAllDivisionsAnnualPowerOnly && (
                                    <div className={`text-small mt24 ${styles.declineUpgradeToPowerContainer}`}>
                                        {areAnyDivisionsAnnualPowerOnly
                                            ? <p data-testid="event-reg.upgrade.decline-annual-power-msg">No thanks, I&apos;m not interested in Annual Power Only divisions and I&apos;d rather pay full price for this event.</p>
                                            : <p data-testid="event-reg.upgrade.decline-upgrade-msg">No thanks, I&apos;d rather pay full price for this event.</p>}
                                        <a className={`${styles.continueWithoutUpgradingLink}`} href="#" onClick={props.handleDeclineUpgradeToPowerClick} data-testid="event-reg.upgrade.continue-without-upgrading-link">Continue without upgrading</a>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {props.step.id === regStepIds.CAMP_USER_SELECTION && (
                <CampRegistrationSelection
                    memberId={user?.id}
                    eventId={event?.id}
                    registrationTypeForCamps={registrationTypeForCamps}
                    setRegistrationTypeForCamps={setRegistrationTypeForCamps}
                    setSidebarFlow={setSidebarFlow}
                    setSelectedDivisionIds={setSelectedDivisionIds}
                    handleClearMultipleDivisionRegistrations={handleClearMultipleDivisionRegistrations}
                    {...props}
                />
            )}
            {props.step.id === regStepIds.CAMP_DIVISION_SELECTION && (
                <CampDivisionSelection
                    {...props}
                    memberId={user?.id}
                    clubVerificationFee={clubVerificationFee}
                    handleMultipleDivisionRegistrations={handleMultipleDivisionRegistrations}
                    multipleDivisionRegistrations={multipleDivisionRegistrations}
                />
            )}
            {props.step.id === regStepIds.CAMP_CONFIRM && (
                <CampConfirmation
                    {...props}
                    memberId={user?.id}
                    clubVerificationFee={clubVerificationFee}
                    multipleDivisionRegistrations={multipleDivisionRegistrations}
                />
            )}
            {props.step.id === regStepIds.PASSWORD && (
                <div className="row">
                    <div className="col">
                        <Form onSubmit={props.handleSubmitPassword}>
                            {(formApi) => {
                                return (
                                    <form onSubmit={formApi.submitForm} id="passwordForm" data-testid="event-reg.password-form.form">
                                        <div className={`${styles.customPasswordStyles}`}>
                                            <p className={`${styles.customTextStyles}`} data-testid="event-reg.password-form.protected-msg">This event is password protected.</p>
                                            <p className={`${styles.customTextMessage} text-small`} data-testid="event-reg.password-form.instructions-msg">Please enter the password or PIN you received from the provider in order to register for this event.</p>
                                            <Input
                                                label="Password"
                                                hiddenLabel={true}
                                                field="answer"
                                                type="password"
                                                placeholder="Password or PIN"
                                                validate={required}
                                                asyncValidate={validatePassword}
                                                asyncErrors={formApi.asyncErrors}
                                                submitting={formApi.submitting}
                                                applyCustomStyle={true}
                                                useCustomErrorIndicator={true}
                                                disableProfanityCheck={true}
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary btn-xl btn-block text-uppercase" data-testid="event-reg.password-form.submit-btn">Continue</button>
                                    </form>
                                );
                            }}
                        </Form>
                    </div>
                </div>
            )}
            {(
                props.step.id === regStepIds.DIVISIONS ||
                props.step.id === regStepIds.DIVISIONS_ADD ||
                props.step.id === regStepIds.DIVISIONS_SWITCH ||
                props.step.id === regStepIds.DIVISIONS_CANCEL ||
                props.step.id === regStepIds.CHANGE_PARTNER
            ) && (
            <EditDivisionRegistration
                clubVerificationFee={clubVerificationFee}
                claimedPlayer={claimedPlayer}
                paymentProviderType={paymentProviderType}
                displayServiceFee={displayServiceFee}
                serviceFee={updatedServiceFee}
                registrationTotal={registrationTotal}
                registrations={registrations}
                {...props}
            />
                )}
            {props.step.id === regStepIds.TEAMMATE && (
                <PartnerSelectionV2
                    teammate={teammate}
                    selectedPlayer={selectedPlayer}
                    isNeedPartnerSelectionEnabled={isNeedPartnerSelectionEnabled}
                    {...props}
                />
            )}
            {props.step.id === regStepIds.CAMP_QUESTIONS && (
                <div className="row">
                    <div className="col">
                        <Form onSubmit={values => props.handleSubmitQuestions({ values })} defaultValues={eventQuestionsDefaultValues}>
                            {(formApi) => {
                                const { values, errors } = formApi;
                                return (
                                    <form onSubmit={formApi.submitForm} id="eventQuestions" data-testid="event-reg.camp-questions.form">
                                        {event.eventQuestions?.length > 0 && (
                                            <div key={`currentMember`}>
                                                <div className="mb8">{`${claimedPlayer.firstName} ${claimedPlayer.lastName}`}</div>
                                                <div className="form-wrapper">
                                                    {eventQuestions?.map((question) => (
                                                        <div
                                                            key={`withquestionid-${question?.id}`}
                                                        >
                                                            {(
                                                                <EventQuestion
                                                                    key={`withid-${claimedPlayer.playerId.toString()}-${question.id}`}
                                                                    label={`${question.text} ${question.required ? '*' : ''}`}
                                                                    field={`withid-${claimedPlayer.playerId.toString()}-${question.id}`}
                                                                    validate={question.required ? required : null}
                                                                    typeId={question.typeId}
                                                                    answer={question.answer}
                                                                    values={values}
                                                                    errors={errors}
                                                                    asyncValidate={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID ? validateUSAPId : null}
                                                                    asyncErrors={formApi.asyncErrors}
                                                                    submitting={formApi.submitting}
                                                                    disableProfanityCheck={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID}
                                                                    required={question.required}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-xl btn-block text-uppercase"
                                            disabled={props.submitting}
                                            data-testid="event-reg.camp-questions.submit-btn"
                                        >
                                            Continue
                                        </button>
                                    </form>
                                );
                            }}
                        </Form>
                    </div>
                </div>
            )}
            {props.step.id === regStepIds.QUESTIONS && (
                <div className="row">
                    <div className="col">
                        <Form onSubmit={values => props.handleSubmitQuestions({ values })} defaultValues={eventQuestionsDefaultValues}>
                            {(formApi) => {
                                const { values, errors } = formApi;
                                return (
                                    <form onSubmit={formApi.submitForm} id="eventQuestions" data-testid="event-reg.questions.form">
                                        {(isRegistered && event.eventQuestions?.length > 0 && (event.currentMember.answers?.length < event.eventQuestions.length)) && (
                                            <div key={`currentMember`}>
                                                <div className="mb8">{`${claimedPlayer.firstName} ${claimedPlayer.lastName}`}</div>
                                                <div className="form-wrapper">
                                                    {eventQuestions?.map((question) => (
                                                        <div
                                                            key={`withquestionid-${question?.id}`}
                                                        >
                                                            {(_.findIndex(event.currentMember.answers, answer => answer.questionId === question.id) === -1) && (
                                                                <EventQuestion
                                                                    key={`withid-${claimedPlayer.playerId.toString()}-${question.id}`}
                                                                    label={`${question.text} ${question.required ? '*' : ''}`}
                                                                    field={`withid-${claimedPlayer.playerId.toString()}-${question.id}`}
                                                                    validate={question.required ? required : null}
                                                                    typeId={question.typeId}
                                                                    answer={question.answer}
                                                                    values={values}
                                                                    errors={errors}
                                                                    asyncValidate={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID ? validateUSAPId : null}
                                                                    asyncErrors={formApi.asyncErrors}
                                                                    submitting={formApi.submitting}
                                                                    disableProfanityCheck={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID}
                                                                    required={question.required}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {playersToShowEventQuestions.map((player) => {
                                            const isPartner = player.id !== (claimedPlayer.playerId ? claimedPlayer.playerId.toString() : '');
                                            const isFreeEvent = event?.eventDivisions.every(division => division.price === freeDivision);
                                            return (
                                                <div key={`player-${player.id}`}>
                                                    {(event?.eventQuestions.length > 0) && (
                                                        <>
                                                            {!(isFreeEvent && player.displayName === needsPartnerText) && !(isPartner && onlyHomeCourtQuestion) && (
                                                                <>
                                                                    <div className="mb8">{`${isPartner ? 'Partner - ' : ''}${player.displayName}`}</div>
                                                                    <div>
                                                                        <div className="form-wrapper">
                                                                            {eventQuestions?.map((question) => (
                                                                                <EventQuestion
                                                                                    key={`${player.key}-${question.id}`}
                                                                                    label={`${question.text} ${question.required ? '*' : ''}`}
                                                                                    field={`${player.key}-${question.id}`}
                                                                                    validate={question.required ? required : null}
                                                                                    typeId={question.typeId}
                                                                                    answer={question.answer}
                                                                                    values={values}
                                                                                    errors={errors}
                                                                                    asyncValidate={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID ? createValidateUsapIdCallback(jwt, event.id, player.displayName) : null}
                                                                                    asyncErrors={formApi.asyncErrors}
                                                                                    submitting={formApi.submitting}
                                                                                    disableProfanityCheck={question.typeId === eventQuestionTypeIds.THIRDPARTY_ID}
                                                                                    isPartner={isPartner}
                                                                                    required={question.required}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-xl btn-block text-uppercase"
                                            disabled={props.submitting}
                                            data-testid="event-reg.questions.submit-btn"
                                        >
                                            Continue
                                        </button>
                                    </form>
                                );
                            }}
                        </Form>
                    </div>
                </div>
            )}

            {props.step.id === regStepIds.CONFIRM && (
                <div className="row">
                    <div className="col">
                        <Form onSubmit={values => props.handleConfirm({ values })}>
                            {(formApi) => {
                                return (
                                    <form onSubmit={formApi.submitForm} id="confirm" data-testid="event-reg.confirm.form">
                                        <div className="form-wrapper">
                                            <div className="mb24">
                                                {event?.eventDivisions.filter(checkDivisionIsSelected).map((division) => {
                                                    const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                                                    return (
                                                        <DivisionSelectionV2
                                                            key={`confirm-division-selection-${division.id}`}
                                                            division={division}
                                                            event={event}
                                                            selected={checkDivisionIsSelected(division)}
                                                            shouldShowGenderInSubTitle={false}
                                                            shouldDisplayTeamPaymentType
                                                            shouldDisplayAgeRestrictionText={false}
                                                            shouldDisplayRangeRestrictionText={false}
                                                            canChangePartner={false}
                                                            disabled
                                                            clubVerificationFee={clubVerificationFee}
                                                            isAdminAddedPaymentRequired={divisionPaymentRequired}
                                                            hideDeclineButton
                                                        />
                                                    );
                                                })}

                                                {isPromoCodesEnabled && isFlexLeagueEvent && props.total > 0 && (
                                                    <div className="row">
                                                        <div className="col-12 text-black text-bold" data-testid="event-reg.promo-code-label">
                                                            HAVE A PROMO CODE?
                                                        </div>
                                                        <div className="col-9">
                                                            <input
                                                                className={`form-control ${styles.promoCodeInput}`}
                                                                type="text"
                                                                value={promoCode}
                                                                onChange={handlePromoCodeChange}
                                                                disabled={promoCodeSuccess}
                                                                data-testid="event-reg.promo-code-input"
                                                            />
                                                        </div>
                                                        <div className="col-3 d-flex justify-content-center align-items-center">
                                                            {promoCodeSuccess ? (
                                                                <button type="button" onClick={handlePromoCodeRemoval} className={`btn btn-default`} data-testid="event-reg.promo-code-remove-btn">REMOVE</button>
                                                            ) : (
                                                                <button type="button" disabled={promoCodeSuccess} onClick={handlePromoCodeApplication} className={`btn btn-default`} data-testid="event-reg.promo-code-apply-btn">APPLY</button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {promoCodeSuccess && (
                                                    <div className={`${styles.promoCodeWrapper}`} data-testid="event-reg.confirm.promo-success-wrapper">
                                                        <div className="col-12 text-black text-bold">
                                                            {promoCode} - <span className={styles.successText} data-testid="event-reg.confirm.promo-applied-text">Applied</span>
                                                        </div>
                                                        <div className="col-12">
                                                            {promoCodeApplicationText}
                                                        </div>
                                                        <div className="col-12">
                                                            Discounts apply to registration fees only.
                                                        </div>
                                                    </div>
                                                )}
                                                {promoCodeFailure && (
                                                    <div className={`${styles.promoCodeWrapper}`} data-testid="event-reg.confirm.promo-failure-wrapper">
                                                        <div className="col-12 text-black text-bold">
                                                            {invalidPromoCodePlaceHolder} - <span className={styles.failureText} data-testid="event-reg.confirm.promo-not-applied-text">Not Applied</span>
                                                        </div>
                                                        <div className="col-12">
                                                            {promoCodeApplicationText}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {event.currency && (
                                                <>
                                                    {!event.allowOffPlatformPayments && (
                                                        <>
                                                            <div className={`text-medium ${styles.lineItems}`} data-testid="event-reg.confirm.subtotal-row">
                                                                {promoCodeSuccess ? (

                                                                    <div className="d-flex align-items-center" data-testid="event-reg.confirm.subtotal-with-promo-label">Subtotal: <InfoButton fixedWidth={250} hoverText={`Subtotal includes Registration Fees and ${feeDescription} if applicable. Discount codes apply to registration fees only. Discounts cannot be combined.`} /></div>

                                                                ) : (

                                                                    <span data-testid="event-reg.confirm.subtotal-label">Subtotal: </span>

                                                                )}
                                                                <span data-testid="event-reg.confirm.subtotal-value">{displayFee(event.currency.symbol, props.subtotal, false)}</span>
                                                            </div>

                                                            {promoCodeSuccess && (
                                                                <p className={`${styles.couponDetails} text-medium`} data-testid="event-reg.confirm.coupon-details">
                                                                    <span data-testid="event-reg.confirm.promo-applied-label">Promo code applied:</span>
                                                                    <span className="float-right" data-testid="event-reg.confirm.promo-discount-value">-{`${props.event.currency.symbol}${totalDiscount}`}</span>
                                                                </p>
                                                            )}
                                                            {displayServiceFee && (
                                                                <div className={`text-medium ${styles.lineItems}`} data-testid="event-reg.confirm.service-fee-row">
                                                                    <div className="d-flex align-items-center" data-testid="event-reg.confirm.service-fee-label">Service Fee:<InfoButton fixedWidth={250} hoverText={`These fees support the essential costs we incur in critical areas like new features, security, reliability/performance, trust and safety and support.`} /> </div>
                                                                    <span className="float-right" data-testid="event-reg.confirm.service-fee-value">{event.currency.symbol}{updatedServiceFee.toFixed(2)}</span>
                                                                </div>
                                                            )}
                                                            <p className="text-medium mb24" data-testid="event-reg.confirm.total-row">
                                                                <span className="text-dark">Total:</span> <span className="text-dark float-right" data-testid="event-reg.confirm.total-value">{displayFee(event.currency.symbol, registrationTotal)}</span>
                                                            </p>
                                                        </>
                                                    )}
                                                    {event.allowOffPlatformPayments && (
                                                        <p className="text-medium mt32 mb24" data-testid="event-reg.confirm.off-platform-total-row"><span className="text-dark" data-testid="event-reg.confirm.off-platform-total-label">Total Fee Due to Club:</span> <span className="text-dark float-right" data-testid="event-reg.confirm.off-platform-total-value">{props.totalOffPlatform.toFixed(2)}</span></p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {showClubJoin && (
                                            <div className="mt32 mb24" data-testid="event-reg.confirm.club-join-wrapper">
                                                <div
                                                    className={`checkbox checkbox-large align-items-start mb24 ${props.requestToJoinClub ? 'selected' : ''}`}
                                                    onClick={() => props.handleRequestToJoinClubChange()}
                                                    {...getClickAttrs({
                                                        category: 'Event Profile CTA',
                                                        location: 'Event Reg Form',
                                                        context: `Registration request to join club`,
                                                    })}
                                                >
                                                    <div className="checkbox-wrapper">
                                                        <div className={`checkbox-check`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                                                    </div>
                                                    <div className="label">
                                                        <div className="text text-small" data-testid="event-reg.confirm.club-join-label">
                                                            {'Become a member of '}
                                                            <a
                                                                href={isClubRedirectEnabled && clubTierTypeId ? `${config.nextAppHost}/${newClubPath}/${event.club.id}` : `${config.appHost}/clubs/${event.club.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(ev) => { ev.stopPropagation(); }}
                                                                data-testid="event-reg.confirm.club-join-link"
                                                            >
                                                                {event.club.name}
                                                            </a>
                                                            <InfoButton hoverText={'Becoming a member of this club allows you to easily follow and register for its upcoming events, get access to private events, and other benefits.'} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {showOptInForEventDeskNotificationsCheckBox && (
                                            <div className="mt32 mb24" data-testid="event-reg.confirm.event-desk-optin-wrapper">
                                                <div
                                                    className={`checkbox checkbox-large align-items-start mb24 ${props.optInForEventDeskNotifications ? 'selected' : ''}`}
                                                    onClick={() => props.handleOptInForEventDeskNotificationsChange()}
                                                    {...getClickAttrs({
                                                        category: 'Event Profile CTA',
                                                        location: 'Event Reg Form',
                                                        context: `Opt in for Event Desk Notifications`,
                                                    })}
                                                >
                                                    <div className="checkbox-wrapper">
                                                        <div className={`checkbox-check`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                                                    </div>
                                                    <div className="label">
                                                        <div className="text text-small" data-testid="event-reg.confirm.event-desk-optin-label">
                                                            Receive SMS texts for court assignments, results, and updates during the event. You can opt out at any time in your player account settings.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {!isFlexLeagueEvent && (
                                            <p className={`text text-small mb24 ${styles.termAndConditions}`} data-testid="event-reg.confirm.terms-msg">
                                                UTR Sports is committed to making pickleball and tennis more accessible, fun, and cost effective. UTR Sports does not screen event organizers or facilities for safety and we encourage all players to register only if you know the organizer and facility. Juniors should be accompanied by an adult for the entire event.
                                            </p>
                                        )}
                                        <button
                                            type="submit"
                                            className={`btn btn-primary btn-xl btn-block text-uppercase ${props.submitting ? 'text-dark' : ''}`}
                                            disabled={props.submitting}
                                            data-testid="event-reg.confirm.submit-btn"
                                        >
                                            {props.submitting && (<i className="fas fa-circle-notch fa-spin" />)}{props.submitting ? 'Processing' : `Agree & Submit`}
                                        </button>
                                    </form>
                                );
                            }}
                        </Form>
                        {props.total !== undefined && !isFlexLeagueEvent && (
                            <div className={styles.termAndConditions} data-testid="event-reg.confirm.standard-terms-wrapper">
                                <span data-testid="event-reg.confirm.standard-terms-text">By registering, I agree to UTR Sports’s <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="event-reg.confirm.tos-link">terms of service</a> and any additional terms provided by the event organizer in the event description. I also agree to provide my contact info to the event organizer solely for the purpose of participating in this event.</span>
                            </div>
                        )}
                        {isFlexLeagueEvent && (
                            <div className={styles.termAndConditions} data-testid="event-reg.confirm.flex-league-terms-wrapper">
                                <div className="mb8" data-testid="event-reg.confirm.flex-league-terms-intro">By registering, I agree to:</div>
                                <ul className="pl16 mb0">
                                    <li className="mb4">UTR Sports’s <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="event-reg.confirm.flex-league-tos-link">terms of service</a> and any additional terms provided in the event description.</li>
                                    <li className="mb4">Provide my contact information to the event organizer(s) and my opponents to facilitate match scheduling.</li>
                                    <li className="mb4">Opt in to receive SMS communication from UTR Sports, which includes your opponent’s contact information and auto-initiated SMS group texts with each opponent to facilitate match scheduling.
                                        <ul><li>You can opt out at any time in your player account settings.</li></ul>
                                    </li>
                                    <li className="mb4">Treat my opponents with respect and handle their contact information confidentially, using it solely for the purpose of communicating directly with them to schedule match play.</li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {props.step.id === regStepIds.PAYMENT && checkoutRequest && stripeAccount && (
                <PaymentStep
                    checkoutRequest={checkoutRequest}
                    stripeAccount={stripeAccount}
                    isPaid={!!props.step.paid}
                    finishPayment={props.finishPayment}
                    event={event}
                    eventFee={registrationTotal}
                    setPaymentTitle={props.setPaymentTitle}
                    setCheckoutSubmittingFlag={props.setCheckoutSubmittingFlag}
                    setSwitchApiResponse={props.setSwitchApiResponse}
                    setSwitchTotalRefund={props.setSwitchTotalRefund}
                    toggleSplashBallLoader={props.toggleSplashBallLoader}
                    submitting={props.submitting}
                    sidebarFlow={sidebarFlow}
                    switchTotalRefund={props.switchTotalRefund}
                    onCheckoutProcessStarted={onCheckoutProcessStarted}
                    onPaymentProcessStarted={props.onPaymentProcessStarted}
                    savedPaymentMethods={props.savedPaymentMethods}
                    multipleDivisionRegistrations={multipleDivisionRegistrations}
                    getMultiRegistrationTotal={getMultiRegistrationTotal}
                    registrationTypeForCamps={registrationTypeForCamps}
                    handleCloseClick={props.handleCloseClick}
                />
            )}
            {props.step.id === regStepIds.PAYMENT_WAIT && checkoutId && (
                <PaymentWaitStep
                    checkoutId={checkoutId}
                />
            )}
        </div>
    );
}

EventRegForm.propTypes = {
    selectedDivisionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    event: eventShape.isRequired,
    user: userShape.isRequired,
    savedPaymentMethods: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        brand: PropTypes.string,
        last4: PropTypes.string,
    })),
    toggleSplashBallLoader: PropTypes.func,
    step: wizardStepShape.isRequired,
    handleValidatePassword: PropTypes.func,
    addUnclaimedEmail: PropTypes.func,
    handleUpgradeToPowerClick: PropTypes.func,
    handleDeclineUpgradeToPowerClick: PropTypes.func,
    onUpgradeComplete: PropTypes.func,
    handleSubmitPassword: PropTypes.func,
    addTeammate: PropTypes.func,
    selectTeammate: PropTypes.func,
    addTeammateToDivision: PropTypes.func,
    toStep: PropTypes.func,
    handleInviteChange: PropTypes.func,
    handleSubmitQuestions: PropTypes.func,
    unRegisteredPlayers: PropTypes.arrayOf(playerShape),
    submitting: PropTypes.bool,
    handleConfirm: PropTypes.func,
    eventEntryFee: PropTypes.number,
    isEventEntryFeePaid: PropTypes.bool,
    eventFee: PropTypes.number,
    total: PropTypes.number,
    subtotal: PropTypes.number,
    totalOffPlatform: PropTypes.number,
    requestToJoinClub: PropTypes.bool,
    handleRequestToJoinClubChange: PropTypes.func,
    optInForEventDeskNotifications: PropTypes.bool,
    handleOptInForEventDeskNotificationsChange: PropTypes.func,
    setCheckoutSubmittingFlag: PropTypes.func,
    stripePopulatePaymentRequestBody: PropTypes.func.isRequired,
    stripePopulateSwitchPaymentRequestBody: PropTypes.func.isRequired,
    onCheckoutProcessStarted: PropTypes.func.isRequired,
    onPaymentProcessStarted: PropTypes.func.isRequired,
    setPaymentTitle: PropTypes.func,
    finishPayment: PropTypes.func,
    handlePromoCodeApplication: PropTypes.func,
    promoCodeSuccess: PropTypes.bool,
    handlePromoCodeRemoval: PropTypes.func,
    promoCodeFailure: PropTypes.bool,
    promoCode: PropTypes.string,
    handlePromoCodeChange: PropTypes.func,
    invalidPromoCodePlaceHolder: PropTypes.string,
    promoCodeApplicationText: PropTypes.string,
    totalDiscount: PropTypes.number,
    serviceFee: PropTypes.number,
    handleCloseClick: PropTypes.func,
    sidebarFlow: PropTypes.string.isRequired,
    switchApiResponse: PropTypes.shape({
        success: PropTypes.bool,
        totalRefund: PropTypes.number,
        switched: PropTypes.arrayOf(PropTypes.shape({
            divisionId: PropTypes.number,
            divisionName: PropTypes.string,
        })),
    }),
    setSwitchApiResponse: PropTypes.func.isRequired,
    switchTotalRefund: PropTypes.number,
    setSwitchTotalRefund: PropTypes.func.isRequired,
    divisionFormatTypeFilter: PropTypes.number,
    setDivisionFormatTypeFilter: PropTypes.func.isRequired,
    switchPlayer2: playerShape,
    setSwitchPlayer2: PropTypes.func.isRequired,
    showPreRegAlternateCopy: PropTypes.bool,
    clubId: PropTypes.number,
    registrationTypeForCamps: PropTypes.string.isRequired,
    setRegistrationTypeForCamps: PropTypes.func.isRequired,
    setSideBarFlow: PropTypes.func.isRequired,
    setSelectedDivisionIds: PropTypes.func.isRequired,
    getMultiRegistrationTotal: PropTypes.func.isRequired,
    handleClearMultipleDivisionRegistrations: PropTypes.func.isRequired,
    multipleDivisionRegistrations: PropTypes.objectOf(PropTypes.shape({
        quantity: PropTypes.number,
        price: PropTypes.number,
    })),
    handleMultipleDivisionRegistrations: PropTypes.func.isRequired,
    location: routerLocationShape.isRequired,
};

EventRegForm.defaultProps = {
    savedPaymentMethods: [],
    unRegisteredPlayers: [],
    toggleSplashBallLoader: () => { },
    handleValidatePassword: () => { },
    addUnclaimedEmail: () => { },
    handleUpgradeToPowerClick: () => { },
    handleDeclineUpgradeToPowerClick: () => { },
    onUpgradeComplete: () => { },
    handleSubmitPassword: () => { },
    addTeammate: () => { },
    selectTeammate: () => { },
    addTeammateToDivision: () => { },
    toStep: () => { },
    handleInviteChange: () => { },
    handleSubmitQuestions: () => { },
    handleConfirm: () => { },
    handleRequestToJoinClubChange: () => { },
    handleOptInForEventDeskNotificationsChange: () => { },
    setCheckoutSubmittingFlag: () => { },
    setPaymentTitle: () => { },
    finishPayment: () => { },
    submitting: false,
    eventEntryFee: 0,
    isEventEntryFeePaid: false,
    eventFee: 0,
    total: 0,
    subtotal: 0,
    totalOffPlatform: 0,
    requestToJoinClub: false,
    optInForEventDeskNotifications: false,
    handlePromoCodeApplication: () => { },
    promoCodeSuccess: false,
    handlePromoCodeRemoval: () => { },
    promoCodeFailure: false,
    promoCode: '',
    handlePromoCodeChange: () => { },
    invalidPromoCodePlaceHolder: '',
    promoCodeApplicationText: '',
    totalDiscount: 0,
    serviceFee: 0,
    handleCloseClick: () => { },
    switchApiResponse: null,
    switchTotalRefund: 0,
    divisionFormatTypeFilter: null,
    switchPlayer2: null,
    showPreRegAlternateCopy: false,
    clubId: null,
    multipleDivisionRegistrations: {},
};

export default EventRegForm;
